import { LayoutDashboard, BarChart2, Users, Shield, FolderKanban, FileText, Binary, FileSignature, Archive, Keyboard, MessageSquare, MessageSquarePlus, MessagesSquare, Building2, Contact, Filter, Inbox, HardDrive, Vault as VaultIcon, KanbanSquare, CalendarRange } from 'lucide-react'
import { WEBSITE_BUILDER_MODULE_SUBPAGES } from '../features/website-builder/websiteBuilderSections'
import type { BadgeTone } from '../components/ui/Badge'

/**
 * Centralised ERP module definitions.
 * `colors` is the module's IDENTITY hue, used ONLY for its icon chip on the
 * launcher grid and the /app cards (M365 model: colorful icons, monochrome
 * chrome). Sidebar active states, headers and buttons are neutral/primary.
 */
export interface ModuleColors {
  /** icon container background, e.g. "bg-emerald-100" */
  iconBg: string
  /** icon color, e.g. "text-emerald-600" */
  iconText: string
}

export interface ModuleSubPage {
  labelKey: string
  labelFallback: string
  path: string
  end?: boolean
  icon?: React.ReactNode
}

export interface ModuleTag {
  /** i18n key for the stamp label */
  labelKey: string
  /** i18n fallback */
  labelFallback: string
  /** Visual tone; defaults to slate */
  tone?: BadgeTone
}

export interface ModuleConfig {
  key: string
  /** i18n key for the title */
  titleKey: string
  /** i18n fallback */
  titleFallback: string
  /** i18n key for the card description */
  descriptionKey: string
  /** i18n fallback */
  descriptionFallback: string
  path: string
  isAlwaysActive?: boolean
  disabled?: boolean
  colors: ModuleColors
  icon: React.ReactNode
  requiredPermission?: string
  subPages?: ModuleSubPage[]
  /** Optional stamps/labels shown on the card (e.g. "Beta", "New") */
  tags?: ModuleTag[]
}

export const MODULE_CONFIGS: ModuleConfig[] = [
  {
    key: 'procurement',
    titleKey: 'dashboard.modules.procurements.title',
    titleFallback: 'Public Procurements',
    descriptionKey: 'dashboard.modules.procurements.desc',
    descriptionFallback: 'Manage bids, track public opportunities, and generate AI insights.',
    path: '/app/procurement',
    requiredPermission: 'module.procurements.view',
    colors: {
      iconBg: 'bg-emerald-100',
      iconText: 'text-emerald-600',
    },
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 8.5L12 2.5L21 8.5Z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 11h16" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M7 11v6.5M10.5 11v6.5M13.5 11v6.5M17 11v6.5" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M5 17.5h14M3.5 20h17M2 22h20" />
      </svg>
    ),
    subPages: [
      { labelKey: 'procurement.tabs.catchUp', labelFallback: 'Notifications', path: '/app/procurement', end: true, icon: <Inbox size={16} /> },
      { labelKey: 'procurement.tabs.filters', labelFallback: 'Filters', path: '/app/procurement/filters', icon: <Filter size={16} /> },
      { labelKey: 'procurement.tracking.nav', labelFallback: 'Tracking', path: '/app/procurement/tracking', icon: <KanbanSquare size={16} /> },
      { labelKey: 'procurement.tabs.analytics', labelFallback: 'Analytics', path: '/app/procurement/analytics', icon: <BarChart2 size={16} /> },
    ],
  },
  {
    key: 'project_management',
    titleKey: 'dashboard.modules.projects.title',
    titleFallback: 'Project Management',
    descriptionKey: 'dashboard.modules.projects.desc',
    descriptionFallback: 'Task tracking, milestones, resource planning.',
    path: '/app/project-management',
    disabled: false,
    requiredPermission: 'module.project_management.view',
    colors: {
      iconBg: 'bg-amber-100',
      iconText: 'text-amber-700',
    },
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
    subPages: [
      { labelKey: 'projectManagement.nav.dashboard', labelFallback: 'Tableau de bord', path: '/app/project-management', end: true, icon: <LayoutDashboard size={16} /> },
      { labelKey: 'projectManagement.nav.projects', labelFallback: 'Projets', path: '/app/project-management/projects', icon: <FolderKanban size={16} /> },
      { labelKey: 'projectManagement.nav.planning', labelFallback: 'Planification globale', path: '/app/project-management/planning', icon: <CalendarRange size={16} /> },
    ],
  },
  {
    key: 'vault',
    titleKey: 'dashboard.modules.vault.title',
    titleFallback: 'Documents administratifs',
    descriptionKey: 'dashboard.modules.vault.desc',
    descriptionFallback: 'Track administrative documents, expiries and renewals.',
    path: '/app/vault',
    requiredPermission: 'module.vault.view',
    colors: {
      iconBg: 'bg-amber-100',
      iconText: 'text-amber-700',
    },
    icon: <VaultIcon className="w-7 h-7" />,
    subPages: [
      { labelKey: 'vault.nav.documents', labelFallback: 'Documents', path: '/app/vault', end: true },
      { labelKey: 'vault.nav.types', labelFallback: 'Types', path: '/app/vault/types' },
    ],
  },
  {
    key: 'tools',
    titleKey: 'dashboard.modules.tools.title',
    titleFallback: 'Tools',
    descriptionKey: 'dashboard.modules.tools.desc',
    descriptionFallback: 'Utility tools: document comparison and more.',
    path: '/app/tools',
    isAlwaysActive: true,
    colors: {
      iconBg: 'bg-orange-100',
      iconText: 'text-orange-600',
    },
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    subPages: [
      { labelKey: 'tools.landing.title', labelFallback: 'Overview', path: '/app/tools', end: true, icon: <LayoutDashboard size={16} /> },
      { labelKey: 'tools.docComparison.title', labelFallback: 'Document Comparison', path: '/app/tools/document-comparison', icon: <FileText size={16} /> },
      { labelKey: 'tools.numToWords.title', labelFallback: 'Number to Words', path: '/app/tools/number-to-words', icon: <Binary size={16} /> },
      { labelKey: 'tools.pdfEditor.title', labelFallback: 'PDF Editor', path: '/app/tools/pdf-editor', icon: <FileSignature size={16} /> },
      { labelKey: 'tools.archiveManager.title', labelFallback: 'Archive Manager', path: '/app/tools/archive-manager', icon: <Archive size={16} /> },
      { labelKey: 'tools.arabicKeyboard.title', labelFallback: 'Clavier Arabe', path: '/app/tools/arabic-keyboard', icon: <Keyboard size={16} /> },
    ],
  },
  {
    key: 'ai_chat',
    titleKey: 'dashboard.modules.aiChat.title',
    titleFallback: 'AI Chat',
    descriptionKey: 'dashboard.modules.aiChat.desc',
    descriptionFallback: 'Conversational AI assistant to help you with your everyday tasks.',
    path: '/app/ai-chat',
    requiredPermission: 'module.ai_chat.view',
    tags: [{ labelKey: 'dashboard.status.beta', labelFallback: 'Beta', tone: 'blue' }],
    colors: {
      iconBg: 'bg-purple-100',
      iconText: 'text-purple-600',
    },
    icon: <MessageSquare className="w-7 h-7" />,
    subPages: [
      { labelKey: 'aiChat.nav.newChat', labelFallback: 'New chat', path: '/app/ai-chat', end: true, icon: <MessageSquarePlus size={16} /> },
      { labelKey: 'aiChat.nav.discussions', labelFallback: 'Discussions', path: '/app/ai-chat/discussions', icon: <MessagesSquare size={16} /> },
    ],
  },
  {
    key: 'organization_management',
    titleKey: 'dashboard.modules.org.title',
    titleFallback: 'Organization Management',
    descriptionKey: 'dashboard.modules.org.desc',
    descriptionFallback: 'Manage users, roles, and platform permissions.',
    path: '/app/organization',
    isAlwaysActive: true,
    colors: {
      iconBg: 'bg-blue-100',
      iconText: 'text-blue-600',
    },
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    subPages: [
      { labelKey: 'org.members.title', labelFallback: 'Members & Invitations', path: '/app/organization/members', icon: <Users size={16} /> },
      { labelKey: 'org.roles.title', labelFallback: 'Roles', path: '/app/organization/roles', icon: <Shield size={16} /> },
      { labelKey: 'org.companyData.title', labelFallback: 'Données société', path: '/app/organization/company-data', icon: <Building2 size={16} /> },
      { labelKey: 'org.storage.title', labelFallback: 'Stockage', path: '/app/organization/storage', icon: <HardDrive size={16} /> },
    ],
  },
  {
    key: 'letterheads',
    titleKey: 'dashboard.modules.letterheads.title',
    titleFallback: 'Letterheads',
    descriptionKey: 'dashboard.modules.letterheads.desc',
    descriptionFallback: 'Create branded letterhead templates with live organization data.',
    path: '/app/letterheads',
    requiredPermission: 'module.letterheads.view',
    tags: [{ labelKey: 'dashboard.status.beta', labelFallback: 'Beta', tone: 'blue' }],
    colors: {
      iconBg: 'bg-purple-100',
      iconText: 'text-purple-600',
    },
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M6 3H16L19 6V20a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M16 3V6H19" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 10h8v2.5H8z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 15h8M8 17.5h5" />
      </svg>
    ),
    subPages: [
      { labelKey: 'letterheads.nav.library', labelFallback: 'Templates', path: '/app/letterheads', end: true },
      { labelKey: 'letterheads.nav.letters', labelFallback: 'Letters', path: '/app/letterheads/letters' },
    ],
  },
  {
    key: 'tiers',
    titleKey: 'dashboard.modules.tiers.title',
    titleFallback: 'Tiers',
    descriptionKey: 'dashboard.modules.tiers.desc',
    descriptionFallback: 'Manage customers, suppliers, prospects, and partners with fiscal identity.',
    path: '/app/tiers',
    requiredPermission: 'module.tiers.view',
    tags: [{ labelKey: 'dashboard.status.beta', labelFallback: 'Beta', tone: 'blue' }],
    colors: {
      iconBg: 'bg-cyan-100',
      iconText: 'text-cyan-600',
    },
    icon: <Contact className="w-7 h-7" />,
    subPages: [
      { labelKey: 'tiers.nav.parties', labelFallback: 'Tiers', path: '/app/tiers', end: true },
    ],
  },
  {
    key: 'website_builder',
    titleKey: 'dashboard.modules.website.title',
    titleFallback: 'Website Builder',
    descriptionKey: 'dashboard.modules.website.desc',
    descriptionFallback: 'Create, manage, and publish your company website — SSL/TLS certificate included.',
    path: '/app/website',
    requiredPermission: 'module.website.view',
    tags: [{ labelKey: 'dashboard.status.beta', labelFallback: 'Beta', tone: 'blue' }],
    colors: {
      iconBg: 'bg-indigo-100',
      iconText: 'text-indigo-600',
    },
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
      </svg>
    ),
    subPages: WEBSITE_BUILDER_MODULE_SUBPAGES,
  },
]
